import { forwardRef, ReactNode, useLayoutEffect } from "react";
import cx from "classnames";

import useCellKeyboardNavigation from "ds/components/Table/hooks/useKeyboardNavigation/useCellKeyboardNavigation";
import { AriaSort } from "types/Aria";
import useEnsureRef from "hooks/useEnsureRef";

import { TableColumnMeta } from "../../types";
import styles from "../styles.module.css";

type TableHeadColumnProps = {
  column: TableColumnMeta;
  stickyRight?: boolean;
  stickyLeft?: boolean;
  ariaSort: AriaSort;
  children: ReactNode;
  extraWidth?: number;
};

const TableHeadColumn = forwardRef<HTMLDivElement, TableHeadColumnProps>(function TableHeadColumn(
  { column, stickyLeft, stickyRight, ariaSort, children, extraWidth = 0 },
  ref
) {
  const keyboardProps = useCellKeyboardNavigation();
  const columnRef = useEnsureRef<HTMLDivElement>(ref);

  useLayoutEffect(() => {
    if (columnRef.current) {
      columnRef.current.style.width = `${column.defaultWidth + extraWidth}px`;
      columnRef.current.style.maxWidth = `${column.maxWidth + extraWidth}px`;
      columnRef.current.style.minWidth = `${column.minWidth + extraWidth}px`;
    }
  }, [column.minWidth, column.maxWidth, column.defaultWidth, columnRef, extraWidth]);

  return (
    <div
      ref={columnRef}
      className={cx(styles.column, {
        [styles.sortable]: column.allowsSorting,
        [styles.interactive]: column.canHide,
        [styles.stickyRight]: stickyRight,
        [styles.stickyLeft]: stickyLeft,
      })}
      role="columnheader"
      aria-sort={ariaSort}
      {...keyboardProps}
    >
      {children}
    </div>
  );
});

TableHeadColumn.displayName = "DS.Table.Head.Column";

export default TableHeadColumn;
